import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  X,
  Play,
  Pause,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Clock,
  Navigation,
  LocateFixed,
  MapPin,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { resolveTrackingState } from "../../utils/trackingStateEngine";
import { snapRouteToRoadVertices } from "../../utils/roadSnap";
import "./TripPlayback.css";

const MIN_POINT_DELAY_MS = 260;
const MAX_POINT_DELAY_MS = 9000;
const DEFAULT_POINT_DELAY_MS = 900;
const MIN_ASSUMED_SPEED_KMH = 4;
const MAX_ASSUMED_SPEED_KMH = 120;
const PLAYBACK_SPEEDS = [0.5, 1, 2, 4, 6, 8, 10];
const PLAYBACK_FRAME_EMIT_MS = 33;
const FOLLOW_RESUME_DELAY_MS = 3000;
const CAMERA_EASE = 0.2;
const CAMERA_UPDATE_MS = 220;
const CAMERA_MIN_PAN_METERS = 12;
const ADDRESS_RESOLVING_LABEL = "Resolving address...";
const ADDRESS_FALLBACK_LABEL = "Address unavailable";
const COLLAPSED_SHEET_HEIGHT = 112;

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
    }
  }
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const toRadians = (deg) => (deg * Math.PI) / 180;
const toDegrees = (rad) => (rad * 180) / Math.PI;
const normalizeAngle = (deg) => ((Number(deg) % 360) + 360) % 360;

const interpolateHeading = (fromDeg, toDeg, t) => {
  const a = normalizeAngle(fromDeg);
  const b = normalizeAngle(toDeg);
  const delta = ((b - a + 540) % 360) - 180;
  return normalizeAngle(a + delta * clamp(t, 0, 1));
};

const haversineMeters = (a, b) => {
  const lat1 = toFiniteNumber(a?.lat);
  const lng1 = toFiniteNumber(a?.lng);
  const lat2 = toFiniteNumber(b?.lat);
  const lng2 = toFiniteNumber(b?.lng);
  if (
    lat1 === null ||
    lng1 === null ||
    lat2 === null ||
    lng2 === null ||
    Math.abs(lat1) > 90 ||
    Math.abs(lat2) > 90 ||
    Math.abs(lng1) > 180 ||
    Math.abs(lng2) > 180
  ) {
    return 0;
  }

  const R = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
};

const bearingBetween = (a, b) => {
  const lat1 = toFiniteNumber(a?.lat);
  const lng1 = toFiniteNumber(a?.lng);
  const lat2 = toFiniteNumber(b?.lat);
  const lng2 = toFiniteNumber(b?.lng);
  if (
    lat1 === null ||
    lng1 === null ||
    lat2 === null ||
    lng2 === null ||
    (lat1 === lat2 && lng1 === lng2)
  ) {
    return null;
  }
  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const lambda1 = toRadians(lng1);
  const lambda2 = toRadians(lng2);
  const y = Math.sin(lambda2 - lambda1) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(lambda2 - lambda1);
  return normalizeAngle(toDegrees(Math.atan2(y, x)));
};

const formatAddress = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address.trim();
  if (address.full) return String(address.full).trim();

  const parts = [
    address.street,
    address.city,
    address.state,
    address.country,
  ].filter(Boolean);

  return parts.join(", ").trim();
};

const normalizeAddressText = (value) => {
  const text = formatAddress(value);
  if (!text) return "";
  if (/^location at\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/i.test(text)) {
    return "";
  }
  return text;
};

const formatDateTime = (value) => {
  const ts = toTimestampMs(value);
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};


const formatDurationLabel = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatStatusLabel = (point) => {
  const explicit = String(point?.vehicleStatus || "").trim();
  if (explicit) return explicit;
  const speed = Number(point?.speed || 0);
  if (speed > 2) return "Running";
  if (point?.accStatus === true) return "Idle";
  return "Stopped";
};

const getPointAddress = (point) =>
  normalizeAddressText(point?.resolvedAddress) ||
  normalizeAddressText(point?.geocodedAddress) ||
  normalizeAddressText(point?.address) ||
  normalizeAddressText(point?.locationAddress) ||
  normalizeAddressText(point?.currentAddress) ||
  "";

const isMeaningfulReplayAddress = (value) => {
  const text = normalizeAddressText(value);
  if (!text) return false;
  return (
    text !== ADDRESS_RESOLVING_LABEL &&
    text !== ADDRESS_FALLBACK_LABEL
  );
};

const getReplayViewportMetrics = (map, position, marginRatio = 0.18) => {
  if (!map || !position || !window.google?.maps?.LatLng) return null;

  const projection = map.getProjection?.();
  const center = map.getCenter?.();
  const zoom = Number(map.getZoom?.());
  const mapDiv = map.getDiv?.();
  const width = Number(mapDiv?.clientWidth || mapDiv?.offsetWidth || 0);
  const height = Number(mapDiv?.clientHeight || mapDiv?.offsetHeight || 0);

  if (!projection?.fromLatLngToPoint || !projection?.fromPointToLatLng) return null;
  if (!center || !Number.isFinite(zoom) || width <= 0 || height <= 0) return null;

  const markerLat = Number(position.lat);
  const markerLng = Number(position.lng);
  if (!Number.isFinite(markerLat) || !Number.isFinite(markerLng)) return null;

  const centerWorld = projection.fromLatLngToPoint(center);
  const markerWorld = projection.fromLatLngToPoint(
    new window.google.maps.LatLng(markerLat, markerLng)
  );

  if (!centerWorld || !markerWorld) return null;

  const scale = 2 ** zoom;
  let dxWorld = markerWorld.x - centerWorld.x;
  if (dxWorld > 0.5) dxWorld -= 1;
  if (dxWorld < -0.5) dxWorld += 1;
  const dyWorld = markerWorld.y - centerWorld.y;

  const pixel = {
    x: width / 2 + dxWorld * scale,
    y: height / 2 + dyWorld * scale,
  };
  const marginX = width * marginRatio;
  const marginY = height * marginRatio;

  return {
    projection,
    markerWorld,
    width,
    height,
    scale,
    pixel,
    safeMinX: marginX,
    safeMaxX: width - marginX,
    safeMinY: marginY,
    safeMaxY: height - marginY,
  };
};

const isReplayMarkerNearViewportEdge = (map, position, marginRatio = 0.18) => {
  const metrics = getReplayViewportMetrics(map, position, marginRatio);
  if (!metrics) return false;

  return (
    metrics.pixel.x < metrics.safeMinX ||
    metrics.pixel.x > metrics.safeMaxX ||
    metrics.pixel.y < metrics.safeMinY ||
    metrics.pixel.y > metrics.safeMaxY
  );
};

const getReplaySafePanTarget = (map, position, marginRatio = 0.18) => {
  const metrics = getReplayViewportMetrics(map, position, marginRatio);
  if (!metrics) return null;
  if (!isReplayMarkerNearViewportEdge(map, position, marginRatio)) return null;

  const {
    projection,
    markerWorld,
    width,
    height,
    scale,
    pixel,
    safeMinX,
    safeMaxX,
    safeMinY,
    safeMaxY,
  } = metrics;

  const safeWidth = safeMaxX - safeMinX;
  const safeHeight = safeMaxY - safeMinY;
  const desiredPixelX =
    pixel.x < safeMinX
      ? safeMinX + safeWidth * 0.35
      : pixel.x > safeMaxX
        ? safeMaxX - safeWidth * 0.35
        : pixel.x;
  const desiredPixelY =
    pixel.y < safeMinY
      ? safeMinY + safeHeight * 0.35
      : pixel.y > safeMaxY
        ? safeMaxY - safeHeight * 0.35
        : pixel.y;

  const desiredCenterWorld = {
    x: markerWorld.x - (desiredPixelX - width / 2) / scale,
    y: markerWorld.y - (desiredPixelY - height / 2) / scale,
  };

  return projection.fromPointToLatLng(desiredCenterWorld);
};

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "12px",
  background: "#fff",
};


const getStatusColor = (statusStr) => {
  const s = String(statusStr || "").toLowerCase();
  if (s === "running" || s === "moving") return "#16a34a";
  if (s === "idle")                       return "#d97706";
  if (s === "stopped")                    return "#dc2626";
  return "#6b7280";
};

const TripPlayback = ({
  tripData,
  onClose,
  map,
  onPlaybackFrame,
  onPanelHeightChange,
  sidebarOffset = 0,
  rightOffset = 0,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [segmentProgress, setSegmentProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [followVehicle, setFollowVehicle] = useState(true);
  const [activeTab, setActiveTab] = useState("logs");
  const [sheetSnap, setSheetSnap] = useState("collapsed"); // "collapsed" | "half" | "full"
  const [dragTrayHeight, setDragTrayHeight] = useState(null);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 900
  );
  const dragRef = useRef({ active:false, startY:0, startSnap:"collapsed", startHeight:COLLAPSED_SHEET_HEIGHT });
  const [logPage, setLogPage] = useState(1);
  const [resolvedAddresses, setResolvedAddresses] = useState({});

  // ── History-picker mode ────────────────────────────────────────────────────
  const [internalRoute, setInternalRoute] = useState(null);
  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);
  const baseUrl = useMemo(
    () =>
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_URL ||
      "https://qylmox.in/api",
    []
  );
  const [histStart, setHistStart] = useState(todayStr);
  const [histEnd, setHistEnd] = useState(todayStr);
  const [histTrips, setHistTrips] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState(null);
  const [histSelId, setHistSelId] = useState(null);
  const [routeLoadingId, setRouteLoadingId] = useState(null);
  const [displayRoute, setDisplayRoute] = useState([]);

  const lastCameraUpdateRef = useRef(0);
  const hasFinalCenteredRef = useRef(false);
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());
  const panelRef = useRef(null);
  const addressRequestCacheRef = useRef({});
  const playbackStateRef = useRef({
    currentIndex: 0,
    segmentProgress: 0,
    playbackSpeed: 1,
    pointDelays: [],
  });
  const isPlayingRef = useRef(false);
  const lastFrameEmitRef = useRef(0);
  const frameEmitTimeoutRef = useRef(null);
  const pendingFrameRef = useRef(null);
  const followResumeTimerRef = useRef(null);
  const mapBoundsFittedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setViewportHeight(window.innerHeight || 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const snapHeights = useMemo(() => {
    const safeViewportHeight = Math.max(640, Number(viewportHeight || 0));
    const full = Math.round(Math.min(safeViewportHeight * 0.82, safeViewportHeight - 88));
    const half = Math.round(Math.min(Math.max(safeViewportHeight * 0.42, 280), full - 96));
    return {
      collapsed: COLLAPSED_SHEET_HEIGHT,
      half,
      full,
    };
  }, [viewportHeight]);

  const normalizedRoute = useMemo(() => {
    const src = internalRoute ?? tripData?.route;
    if (!Array.isArray(src)) return [];

    const normalized = src
      .map((point, index) => {
        const lat = toFiniteNumber(point?.lat ?? point?.latitude);
        const lng = toFiniteNumber(point?.lng ?? point?.longitude);
        if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
        return {
          ...point,
          sourceIndex: index,
          lat,
          lng,
          speed: Math.max(0, toFiniteNumber(point?.speed) || 0),
          heading: toFiniteNumber(point?.heading ?? point?.course ?? point?.bearing),
          course: toFiniteNumber(point?.course),
          bearing: toFiniteNumber(point?.bearing),
          accStatus: point?.accStatus ?? point?.acc ?? point?.ignition ?? null,
          timestampMs: toTimestampMs(point?.timestamp ?? point?.gpsTime ?? point?.packetTime),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const aTs = Number(a?.timestampMs);
        const bTs = Number(b?.timestampMs);
        const aHasTs = Number.isFinite(aTs) && aTs > 0;
        const bHasTs = Number.isFinite(bTs) && bTs > 0;
        if (aHasTs && bHasTs) {
          if (aTs !== bTs) return aTs - bTs;
          return Number(a.sourceIndex || 0) - Number(b.sourceIndex || 0);
        }
        if (aHasTs) return -1;
        if (bHasTs) return 1;
        return Number(a.sourceIndex || 0) - Number(b.sourceIndex || 0);
      });

    const cleaned = [];
    for (const point of normalized) {
      const prev = cleaned[cleaned.length - 1];
      if (!prev) {
        cleaned.push(point);
        continue;
      }

      const distanceMeters = haversineMeters(prev, point);
      const deltaMs =
        prev.timestampMs && point.timestampMs && point.timestampMs > prev.timestampMs
          ? point.timestampMs - prev.timestampMs
          : null;
      const derivedKmh = deltaMs ? (distanceMeters / (deltaMs / 1000)) * 3.6 : null;
      const packetKmh = Math.max(prev.speed || 0, point.speed || 0);

      const isImpossibleJump =
        (distanceMeters > 1400 &&
          ((derivedKmh && derivedKmh > Math.max(150, packetKmh * 3 + 25)) ||
            (!deltaMs && distanceMeters > 1000))) ||
        (deltaMs && deltaMs < 5000 && distanceMeters > 260 && packetKmh < 20);

      if (distanceMeters < 1.2 && (!deltaMs || deltaMs < 5000)) continue;
      if (!isImpossibleJump) cleaned.push(point);
    }

    let prevTracking = null;
    return cleaned.map((point, idx) => {
      const prevPoint = cleaned[idx - 1];
      const fallbackTs = (prevPoint?.timestampMs || Date.now()) + DEFAULT_POINT_DELAY_MS;
      const timestampMs = point.timestampMs || fallbackTs;

      const tracking = resolveTrackingState({
        prev: prevTracking,
        speed: point.speed,
        accStatus: point.accStatus,
        timestampMs,
        latitude: point.lat,
        longitude: point.lng,
        heading: point.heading ?? point.course ?? point.bearing ?? 0,
        vehicleStatus: point.vehicleStatus ?? point.status ?? null,
        movementState: point.movementState ?? point.movement_state ?? null,
        direction: point.direction ?? null,
        turnType: point.turnType ?? point.turn_type ?? null,
      });
      prevTracking = tracking;

      return {
        ...point,
        timestampMs,
        heading: tracking.heading,
        movementState: tracking.movementState,
        vehicleStatus: tracking.vehicleStatus,
        direction: tracking.direction,
        turnType: tracking.turnType,
        accStatus: tracking.accStatus,
      };
    });
  }, [tripData, internalRoute]);

  useEffect(() => {
    let cancelled = false;

    if (!normalizedRoute.length) {
      setDisplayRoute([]);
      return undefined;
    }

    // Show the usable GPS route immediately. Road snapping is opt-in because
    // a long history otherwise creates many third-party requests after load.
    setDisplayRoute(normalizedRoute);

    const shouldSnap =
      process.env.REACT_APP_ENABLE_HISTORY_ROAD_SNAP === "true" &&
      Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY) &&
      normalizedRoute.length > 1;

    if (!shouldSnap) {
      return undefined;
    }

    (async () => {
      const snapped = await snapRouteToRoadVertices(normalizedRoute);
      if (cancelled || !Array.isArray(snapped) || !snapped.length) return;
      setDisplayRoute(snapped);
    })();

    return () => {
      cancelled = true;
    };
  }, [normalizedRoute]);

  const route = displayRoute;

  useEffect(() => {
    setResolvedAddresses({});
    addressRequestCacheRef.current = {};
  }, [tripData?.id, tripData?.date, tripData?.startTime, tripData?.endTime]);

  // ── History-picker: fetch trip list ────────────────────────────────────────
  const fetchHistoryTrips = useCallback(async (start, end) => {
    const deviceId = tripData?.deviceId;
    if (!deviceId) return;
    const token = localStorage.getItem("token");
    setHistLoading(true); setHistError(null); setHistTrips([]); setHistSelId(null);
    try {
      const url = `${baseUrl}/history/${deviceId}/report?startDate=${start}&endDate=${end}&mode=summary`;
      const resp = await fetch(url, { headers: { Authorization: token ? `Bearer ${token}` : "", "Content-Type": "application/json" } });
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      const days = Array.isArray(data.days) ? data.days : (Array.isArray(data.trips) ? data.trips : []);
      const list = days.map((d) => ({
        id: d._id || d.date || String(Math.random()),
        date: d.date,
        startTime: d.startTime || `${d.date}T00:00:00Z`,
        endTime: d.endTime || `${d.date}T23:59:59Z`,
        distance: Number(d.distanceRawKm ?? d.distance ?? 0).toFixed(1),
        duration: Number(d.movingTime ?? d.duration ?? 0),
        maxSpeed: Number(d.maxSpeedRawKmh ?? d.maxSpeed ?? 0).toFixed(0),
      }));
      setHistTrips(list);
      if (list.length) setHistSelId(list[0].id);
    } catch (e) {
      setHistError(e.message || "Failed");
    } finally {
      setHistLoading(false);
    }
  }, [baseUrl, tripData?.deviceId]);

  // ── History-picker: fetch route for selected trip ──────────────────────────
  const loadTripRoute = useCallback(async (trip) => {
    const deviceId = tripData?.deviceId;
    if (!trip || !deviceId) return;
    const token = localStorage.getItem("token");
    setRouteLoadingId(trip.id);
    try {
      const p = new URLSearchParams({ date: trip.date });
      if (trip.startTime) p.set("startTs", new Date(trip.startTime).toISOString());
      if (trip.endTime) p.set("endTs", new Date(trip.endTime).toISOString());
      const resp = await fetch(`${baseUrl}/history/${deviceId}/playback?${p}`, { headers: { Authorization: token ? `Bearer ${token}` : "" } });
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      const points = Array.isArray(data.points) ? data.points : [];
      setInternalRoute(points);
      setIsPlaying(false); setCurrentIndex(0); setSegmentProgress(0); setProgress(0);
    } catch (e) {
      setHistError(e.message || "Route load failed");
    } finally {
      setRouteLoadingId(null);
    }
  }, [baseUrl, tripData?.deviceId]);

  // Auto-load trips when historyMode opens
  useEffect(() => {
    if (tripData?.historyMode && !internalRoute) {
      fetchHistoryTrips(histStart, histEnd);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripData?.historyMode, tripData?.deviceId]);

  useEffect(() => {
    if (!route.length) return;
    if (!tripData?.deviceId) return;

    const pendingPoints = [];
    const seenIndices = new Set();
    const checkpointStep = Math.max(1, Math.floor(route.length / 8));
    const candidateIndices = new Set([
      0,
      route.length - 1,
    ]);

    for (let idx = 0; idx < route.length; idx += checkpointStep) {
      candidateIndices.add(idx);
    }

    candidateIndices.forEach((idx) => {
      const point = route[idx];
      if (!point) return;
      const sourceIndex = Number(point?.sourceIndex ?? idx);
      if (seenIndices.has(sourceIndex) || getPointAddress(point)) return;
      const lat = toFiniteNumber(point?.lat ?? point?.latitude);
      const lng = toFiniteNumber(point?.lng ?? point?.longitude);
      if (lat === null || lng === null) return;
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      if (addressRequestCacheRef.current[key]?.status === "resolved") return;
      seenIndices.add(sourceIndex);
      pendingPoints.push({ point, sourceIndex, lat, lng, cacheKey: key });
    });

    if (!pendingPoints.length) return undefined;

    let cancelled = false;

    const resolveAddress = async (lat, lng) => {
      const cacheKey = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      const existing = addressRequestCacheRef.current[cacheKey];
      if (existing?.status === "resolved") {
        return existing.value;
      }
      if (existing?.status === "pending" && existing.promise) {
        return existing.promise;
      }

      const token = localStorage.getItem("token");
      const request = (async () => {
        try {
          const response = await fetch(
            `${baseUrl}/devices/${tripData.deviceId}/address?lat=${lat}&lng=${lng}`,
            {
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}`, "x-auth-token": token } : {}),
              },
            }
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          const address =
            normalizeAddressText(data?.address?.full) ||
            normalizeAddressText(data?.address?.short) ||
            normalizeAddressText(data?.address);

          const resolvedValue = address || ADDRESS_FALLBACK_LABEL;
          addressRequestCacheRef.current[cacheKey] = {
            status: "resolved",
            value: resolvedValue,
          };
          return resolvedValue;
        } catch {
          addressRequestCacheRef.current[cacheKey] = {
            status: "resolved",
            value: ADDRESS_FALLBACK_LABEL,
          };
          return ADDRESS_FALLBACK_LABEL;
        }
      })();

      addressRequestCacheRef.current[cacheKey] = {
        status: "pending",
        promise: request,
      };

      try {
        return await request;
      } catch {
        return ADDRESS_FALLBACK_LABEL;
      }
    };

    (async () => {
      // A sampled address set is enough for the playback UI. Resolve it in
      // parallel and update React once instead of rerendering after every
      // geocoding response.
      setResolvedAddresses((prev) => ({
        ...prev,
        ...Object.fromEntries(
          pendingPoints.map((pending) => [pending.sourceIndex, ADDRESS_RESOLVING_LABEL])
        ),
      }));

      const resolved = await Promise.all(
        pendingPoints.map(async (pending) => [
          pending.sourceIndex,
          await resolveAddress(pending.lat, pending.lng),
        ])
      );
      if (cancelled) return;

      setResolvedAddresses((prev) => ({ ...prev, ...Object.fromEntries(resolved) }));
    })();

    return () => {
      cancelled = true;
    };
  }, [baseUrl, route, tripData?.deviceId]);

  const routeWithResolvedAddresses = useMemo(
    () =>
      route.map((point, routeIndex) => ({
        ...point,
        routeIndex,
        resolvedAddress: resolvedAddresses[Number(point?.sourceIndex ?? 0)] || point?.resolvedAddress,
      })),
    [resolvedAddresses, route]
  );

  const fallbackReplayAddressesByRouteIndex = useMemo(() => {
    const directAddresses = routeWithResolvedAddresses.map((point) => getPointAddress(point));
    const previousAddresses = new Array(directAddresses.length).fill("");
    const nextAddresses = new Array(directAddresses.length).fill("");
    let lastKnownAddress = "";

    for (let index = 0; index < directAddresses.length; index += 1) {
      previousAddresses[index] = lastKnownAddress;
      if (isMeaningfulReplayAddress(directAddresses[index])) {
        lastKnownAddress = directAddresses[index];
      }
    }

    lastKnownAddress = "";
    for (let index = directAddresses.length - 1; index >= 0; index -= 1) {
      nextAddresses[index] = lastKnownAddress;
      if (isMeaningfulReplayAddress(directAddresses[index])) {
        lastKnownAddress = directAddresses[index];
      }
    }

    return directAddresses.map((_, index) => previousAddresses[index] || nextAddresses[index] || "");
  }, [routeWithResolvedAddresses]);

  const getReplayAddress = useCallback(
    (point) => {
      const resolved = getPointAddress(point);
      if (resolved) return resolved;

      const sourceIndex = Number(point?.sourceIndex ?? 0);
      const pendingValue = resolvedAddresses[sourceIndex];
      const routeIndex = Number(point?.routeIndex);
      const lastKnownAddress =
        Number.isInteger(routeIndex) && routeIndex >= 0
          ? fallbackReplayAddressesByRouteIndex[routeIndex] || ""
          : "";

      if (isMeaningfulReplayAddress(pendingValue)) {
        return pendingValue;
      }

      if (lastKnownAddress) {
        return lastKnownAddress;
      }

      if (pendingValue === ADDRESS_RESOLVING_LABEL) {
        return ADDRESS_RESOLVING_LABEL;
      }

      return pendingValue || ADDRESS_FALLBACK_LABEL;
    },
    [fallbackReplayAddressesByRouteIndex, resolvedAddresses]
  );

  const pointDelays = useMemo(() => {
    if (routeWithResolvedAddresses.length < 2) return [];
    return routeWithResolvedAddresses.slice(0, -1).map((curr, idx) => {
      const next = routeWithResolvedAddresses[idx + 1];
      const distanceMeters = haversineMeters(curr, next);
      const tsDiffMs =
        curr.timestampMs && next.timestampMs && next.timestampMs > curr.timestampMs
          ? next.timestampMs - curr.timestampMs
          : null;

      if (tsDiffMs) {
        return clamp(tsDiffMs, MIN_POINT_DELAY_MS, MAX_POINT_DELAY_MS);
      }

      const speedKmh = clamp(
        Math.max(curr.speed || 0, next.speed || 0) || MIN_ASSUMED_SPEED_KMH,
        MIN_ASSUMED_SPEED_KMH,
        MAX_ASSUMED_SPEED_KMH
      );
      const durationMs = (distanceMeters / (speedKmh / 3.6)) * 1000;
      return clamp(durationMs || DEFAULT_POINT_DELAY_MS, MIN_POINT_DELAY_MS, MAX_POINT_DELAY_MS);
    });
  }, [routeWithResolvedAddresses]);

  useEffect(() => {
    setCurrentIndex(0);
    setSegmentProgress(0);
    setProgress(0);
    setIsPlaying(false);
    hasFinalCenteredRef.current = false;
    mapBoundsFittedRef.current = false;
  }, [tripData?.id, tripData?.date, normalizedRoute]);

  useEffect(() => {
    playbackStateRef.current.currentIndex = currentIndex;
    playbackStateRef.current.segmentProgress = segmentProgress;
  }, [currentIndex, segmentProgress]);

  useEffect(() => {
    playbackStateRef.current.playbackSpeed = playbackSpeed;
  }, [playbackSpeed]);

  useEffect(() => {
    playbackStateRef.current.pointDelays = pointDelays;
  }, [pointDelays]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    setCurrentIndex((idx) => Math.min(idx, Math.max(0, route.length - 1)));
  }, [route.length]);

  useEffect(() => {
    if (route.length <= 1) {
      setProgress(0);
      return;
    }
    setProgress(((currentIndex + segmentProgress) / (route.length - 1)) * 100);
  }, [currentIndex, route.length, segmentProgress]);

  useEffect(() => {
    if (!isPlaying || route.length < 2) return undefined;
    if (playbackStateRef.current.currentIndex >= route.length - 1) {
      setIsPlaying(false);
      return undefined;
    }

    const animate = () => {
      const now = Date.now();
      let remainingMs = Math.max(0, now - lastUpdateRef.current);
      let idx = playbackStateRef.current.currentIndex;
      let seg = playbackStateRef.current.segmentProgress;
      const nextPointDelays = playbackStateRef.current.pointDelays;
      const nextPlaybackSpeed = playbackStateRef.current.playbackSpeed;

      while (remainingMs > 0 && idx < route.length - 1) {
        const baseDelay = nextPointDelays[idx] || DEFAULT_POINT_DELAY_MS;
        const segmentMs = clamp(
          baseDelay / Math.max(0.25, nextPlaybackSpeed),
          MIN_POINT_DELAY_MS,
          MAX_POINT_DELAY_MS
        );
        const leftMs = (1 - seg) * segmentMs;

        if (remainingMs < leftMs) {
          seg += remainingMs / segmentMs;
          remainingMs = 0;
        } else {
          remainingMs -= leftMs;
          idx += 1;
          seg = 0;
        }
      }

      if (idx >= route.length - 1) {
        idx = route.length - 1;
        seg = 0;
        setIsPlaying(false);
      }

      playbackStateRef.current.currentIndex = idx;
      playbackStateRef.current.segmentProgress = seg;
      setCurrentIndex((prev) => (prev === idx ? prev : idx));
      setSegmentProgress((prev) => (Math.abs(prev - seg) < 0.001 ? prev : seg));
      lastUpdateRef.current = now;

      if (isPlayingRef.current && idx < route.length - 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    lastUpdateRef.current = Date.now();
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, route.length]);

  useEffect(() => {
    if (!map || route.length === 0 || !window.google?.maps?.LatLngBounds) return;
    if (mapBoundsFittedRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    route.forEach((point) => {
      bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
    });
    map.fitBounds(bounds);
    mapBoundsFittedRef.current = true;
  }, [map, route]);

  useEffect(() => {
    if (!map) return undefined;
    const scheduleFollowResume = () => {
      if (followResumeTimerRef.current) {
        window.clearTimeout(followResumeTimerRef.current);
      }
      followResumeTimerRef.current = window.setTimeout(() => {
        followResumeTimerRef.current = null;
        if (isPlayingRef.current) {
          setFollowVehicle(true);
        }
      }, FOLLOW_RESUME_DELAY_MS);
    };

    const disableFollowTemporarily = () => {
      setFollowVehicle((prev) => (prev ? false : prev));
      scheduleFollowResume();
    };

    const dragStart = map.addListener("dragstart", disableFollowTemporarily);
    const zoomChanged = map.addListener("zoom_changed", () => {
      if (isPlayingRef.current) disableFollowTemporarily();
    });
    return () => {
      dragStart?.remove();
      zoomChanged?.remove();
      if (followResumeTimerRef.current) {
        window.clearTimeout(followResumeTimerRef.current);
        followResumeTimerRef.current = null;
      }
    };
  }, [map]);

  const stops = useMemo(() => {
    if (!route.length) return [];
    return route
      .filter((point) => (point.speed || 0) < 2 && point.movementState !== "moving")
      .slice(0, 24);
  }, [route]);

  const currentPosition = useMemo(() => {
    const curr = route[currentIndex] || route[0];
    const next = route[Math.min(currentIndex + 1, route.length - 1)] || curr;
    if (!curr || !next) return null;

    const t = clamp(segmentProgress, 0, 1);
    const lat = curr.lat + (next.lat - curr.lat) * t;
    const lng = curr.lng + (next.lng - curr.lng) * t;
    const speed = (curr.speed || 0) + ((next.speed || 0) - (curr.speed || 0)) * t;
    const segmentHeading = bearingBetween(curr, next);
    const heading =
      segmentHeading ??
      interpolateHeading(curr.heading || 0, next.heading || curr.heading || 0, t);
    const meta = t >= 0.5 ? next : curr;

    return {
      lat,
      lng,
      speed,
      heading,
      timestamp: curr.timestamp || curr.timestampMs || null,
      vehicleStatus: meta.vehicleStatus || "running",
      movementState: meta.movementState || "moving",
      direction: meta.direction || null,
      turnType: meta.turnType || null,
      accStatus: meta.accStatus,
    };
  }, [route, currentIndex, segmentProgress]);

  const currentPointMeta = useMemo(
    () =>
      routeWithResolvedAddresses[
        Math.min(currentIndex, Math.max(0, routeWithResolvedAddresses.length - 1))
      ] || null,
    [routeWithResolvedAddresses, currentIndex]
  );

  const easeCameraToPosition = useCallback(
    (position, { force = false } = {}) => {
      if (!map || !position) return;

      const center = map.getCenter?.();
      if (!center) {
        map.panTo({ lat: position.lat, lng: position.lng });
        return;
      }

      const currentCenter = { lat: center.lat(), lng: center.lng() };
      const distanceMeters = haversineMeters(currentCenter, position);
      if (!force && distanceMeters < CAMERA_MIN_PAN_METERS) return;

      const nextCenter = force
        ? position
        : {
            lat: currentCenter.lat + (position.lat - currentCenter.lat) * CAMERA_EASE,
            lng: currentCenter.lng + (position.lng - currentCenter.lng) * CAMERA_EASE,
          };

      map.panTo(nextCenter);
    },
    [map]
  );

  useEffect(() => {
    if (!currentPosition) return;
    if (map && followVehicle) {
      const nowTime = Date.now();
      const isTripEnd =
        currentIndex >= route.length - 1 && clamp(segmentProgress, 0, 1) >= 0.98;

      if (isTripEnd && !hasFinalCenteredRef.current) {
        easeCameraToPosition(
          { lat: currentPosition.lat, lng: currentPosition.lng },
          { force: true }
        );
        hasFinalCenteredRef.current = true;
        lastCameraUpdateRef.current = nowTime;
      } else if (!isTripEnd) {
        hasFinalCenteredRef.current = false;
        if (nowTime - lastCameraUpdateRef.current > CAMERA_UPDATE_MS) {
          const nearViewportEdge = isReplayMarkerNearViewportEdge(
            map,
            currentPosition,
            0.18
          );

          if (nearViewportEdge) {
            const safePanTarget = getReplaySafePanTarget(
              map,
              currentPosition,
              0.18
            );

            if (safePanTarget) {
              easeCameraToPosition(
                {
                  lat: safePanTarget.lat(),
                  lng: safePanTarget.lng(),
                },
                { force: true }
              );
            } else {
              easeCameraToPosition({
                lat: currentPosition.lat,
                lng: currentPosition.lng,
              });
            }
            lastCameraUpdateRef.current = nowTime;
          }
        }
      }
    }

    const nextFrame = {
      deviceId: tripData?.deviceId || tripData?.vehicleId || null,
      imei: tripData?.imei || null,
      route,
      currentIndex,
      currentPosition,
      stops,
      status: currentPosition.vehicleStatus || "running",
      vehicleType: tripData?.vehicleType || tripData?.deviceIcon || "car",
      deviceIcon: tripData?.deviceIcon || tripData?.vehicleType || "car",
    };

    pendingFrameRef.current = nextFrame;
    if (typeof onPlaybackFrame === "function") {
      const now = Date.now();
      const emitFrame = () => {
        frameEmitTimeoutRef.current = null;
        lastFrameEmitRef.current = Date.now();
        onPlaybackFrame(pendingFrameRef.current);
      };

      if (now - lastFrameEmitRef.current >= PLAYBACK_FRAME_EMIT_MS) {
        emitFrame();
      } else if (!frameEmitTimeoutRef.current) {
        frameEmitTimeoutRef.current = window.setTimeout(
          emitFrame,
          PLAYBACK_FRAME_EMIT_MS - (now - lastFrameEmitRef.current)
        );
      }
    }
  }, [
    currentPosition,
    currentIndex,
    easeCameraToPosition,
    followVehicle,
    map,
    onPlaybackFrame,
    route,
    segmentProgress,
    stops,
    tripData,
  ]);

  useEffect(() => {
    return () => {
      if (frameEmitTimeoutRef.current) {
        window.clearTimeout(frameEmitTimeoutRef.current);
        frameEmitTimeoutRef.current = null;
      }
      if (followResumeTimerRef.current) {
        window.clearTimeout(followResumeTimerRef.current);
        followResumeTimerRef.current = null;
      }
      onPlaybackFrame?.(null);
      mapBoundsFittedRef.current = false;
    };
  }, [onPlaybackFrame]);

  useEffect(() => {
    if (typeof onPanelHeightChange !== "function") return undefined;

    const el = panelRef.current;
    if (!el) {
      onPanelHeightChange(0);
      return undefined;
    }

    const update = () => {
      const rect = el.getBoundingClientRect?.();
      onPanelHeightChange(Math.max(0, Math.round(rect?.height || 0)));
    };

    update();
    if (typeof ResizeObserver !== "undefined") {
      const obs = new ResizeObserver(() => update());
      obs.observe(el);
      return () => {
        obs.disconnect();
        onPanelHeightChange(0);
      };
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      onPanelHeightChange(0);
    };
  }, [onPanelHeightChange, sheetSnap, activeTab]);

  const summaryStats = useMemo(() => {
    if (!route.length) {
      return {
        pointCount: 0,
        topSpeed: 0,
        avgSpeed: 0,
        idlePoints: 0,
      };
    }

    return {
      pointCount: routeWithResolvedAddresses.length,
      topSpeed: routeWithResolvedAddresses.reduce((max, point) => Math.max(max, Number(point?.speed || 0)), 0),
      avgSpeed:
        routeWithResolvedAddresses.reduce((sum, point) => sum + Number(point?.speed || 0), 0) / Math.max(1, routeWithResolvedAddresses.length),
      idlePoints: routeWithResolvedAddresses.filter((point) => Number(point?.speed || 0) <= 2).length,
    };
  }, [route.length, routeWithResolvedAddresses]);

  const logRows = useMemo(
    () =>
      routeWithResolvedAddresses.map((point, index) => ({
        id: `${point.sourceIndex || index}-${point.timestampMs || index}`,
        index,
        status: formatStatusLabel(point),
        fixTime: formatDateTime(point.timestampMs || point.timestamp),
        latitude: Number(point.lat).toFixed(6),
        longitude: Number(point.lng).toFixed(6),
        speed: `${Math.round(Number(point.speed || 0))} km/h`,
        address: getReplayAddress(point),
      })),
    [getReplayAddress, routeWithResolvedAddresses]
  );

  const LOG_PAGE_SIZE = 10;
  const logRowCount = logRows.length;
  const logPageCount = Math.max(1, Math.ceil(logRowCount / LOG_PAGE_SIZE));
  const safeLogPage = clamp(logPage, 1, logPageCount);
  const logStartIndex = (safeLogPage - 1) * LOG_PAGE_SIZE;
  const logEndIndex = Math.min(logStartIndex + LOG_PAGE_SIZE, logRowCount);
  const pagedLogRows = useMemo(
    () => logRows.slice(logStartIndex, logEndIndex),
    [logRows, logStartIndex, logEndIndex]
  );

  useEffect(() => {
    setLogPage(1);
  }, [tripData?.id, tripData?.date, logRowCount]);

  const logPageButtons = useMemo(() => {
    const buttons = [];
    const push = (value, label = null) =>
      buttons.push({ key: String(label ?? value), value, label: label ?? String(value) });

    push(1);
    const start = Math.max(2, safeLogPage - 2);
    const end = Math.min(logPageCount - 1, safeLogPage + 2);
    if (start > 2) buttons.push({ key: "ellipsis-left", value: null, label: "..." });
    for (let i = start; i <= end; i++) push(i);
    if (end < logPageCount - 1) buttons.push({ key: "ellipsis-right", value: null, label: "..." });
    if (logPageCount > 1) push(logPageCount);
    return buttons;
  }, [safeLogPage, logPageCount]);

  const parkingRows = useMemo(() => {
    const sessions = [];
    let currentSession = null;

    routeWithResolvedAddresses.forEach((point, index) => {
      const parked = Number(point?.speed || 0) <= 2 && point?.movementState !== "moving";
      if (!parked) {
        if (currentSession) {
          sessions.push(currentSession);
          currentSession = null;
        }
        return;
      }

      if (!currentSession) {
        currentSession = {
          id: `park-${index}`,
          startIndex: index,
          endIndex: index,
          startTs: point.timestampMs || point.timestamp,
          endTs: point.timestampMs || point.timestamp,
          lat: point.lat,
          lng: point.lng,
          address: getReplayAddress(point),
        };
        return;
      }

      currentSession.endIndex = index;
      currentSession.endTs = point.timestampMs || point.timestamp;
      if (!currentSession.address || currentSession.address === ADDRESS_RESOLVING_LABEL) {
        currentSession.address = getReplayAddress(point);
      }
    });

    if (currentSession) sessions.push(currentSession);

    return sessions
      .map((session) => ({
        ...session,
        durationSeconds: Math.max(
          0,
          ((toTimestampMs(session.endTs) || 0) - (toTimestampMs(session.startTs) || 0)) / 1000
        ),
      }))
      .filter((session) => session.endIndex > session.startIndex || session.durationSeconds > 0);
  }, [getReplayAddress, routeWithResolvedAddresses]);

  const eventRows = useMemo(
    () =>
      routeWithResolvedAddresses
        .map((point, index) => ({
          id: `evt-${index}`,
          index,
          type:
            point?.eventType ||
            point?.alarmType ||
            point?.event ||
            point?.alert ||
            "",
          time: formatDateTime(point.timestampMs || point.timestamp),
          speed: `${Math.round(Number(point.speed || 0))} km/h`,
          address: getReplayAddress(point),
        }))
        .filter((row) => row.type),
    [getReplayAddress, routeWithResolvedAddresses]
  );

  const handlePlayPause = () => {
    if (currentIndex >= route.length - 1) {
      setCurrentIndex(0);
      setSegmentProgress(0);
      setProgress(0);
      hasFinalCenteredRef.current = false;
    }
    setIsPlaying((prev) => !prev);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentIndex(0);
    setSegmentProgress(0);
    setProgress(0);
    hasFinalCenteredRef.current = false;
    if (map && route[0]) {
      map.panTo({ lat: route[0].lat, lng: route[0].lng });
    }
  };

  const handleStepBack = () => {
    setIsPlaying(false);
    setSegmentProgress(0);
    setCurrentIndex((idx) => Math.max(0, idx - 1));
  };

  const handleStepForward = () => {
    setIsPlaying(false);
    setSegmentProgress(0);
    setCurrentIndex((idx) => Math.min(route.length - 1, idx + 1));
  };

  const jumpToRow = (index) => {
    setIsPlaying(false);
    setSegmentProgress(0);
    setCurrentIndex(clamp(index, 0, Math.max(0, route.length - 1)));
  };

  const handleProgressClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clamp(clickX / rect.width, 0, 1);
    const scaled = percentage * Math.max(0, route.length - 1);
    const nextIndex = Math.floor(scaled);
    const nextSeg = scaled - nextIndex;
    setCurrentIndex(nextIndex);
    setSegmentProgress(nextSeg);
    setProgress(percentage * 100);
  };


  // ── Bottom-sheet drag + wheel ──────────────────────────────────────────────
  const onHandlePointerDown = useCallback((e) => {
    const startHeight = dragTrayHeight ?? snapHeights[sheetSnap] ?? COLLAPSED_SHEET_HEIGHT;
    dragRef.current = {
      active: true,
      startY: e.clientY,
      startSnap: sheetSnap,
      startHeight,
    };
    setIsDraggingSheet(true);
    setDragTrayHeight(startHeight);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [dragTrayHeight, sheetSnap, snapHeights]);

  const finishSheetDrag = useCallback((clientY) => {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.startY - clientY;
    const currentHeight = clamp(
      dragRef.current.startHeight + delta,
      snapHeights.collapsed,
      snapHeights.full
    );
    const snapEntries = Object.entries(snapHeights);
    const nearestSnap = snapEntries.reduce((best, [key, value]) => {
      if (!best) return { key, distance: Math.abs(currentHeight - value) };
      const distance = Math.abs(currentHeight - value);
      return distance < best.distance ? { key, distance } : best;
    }, null);

    dragRef.current.active = false;
    setIsDraggingSheet(false);
    setDragTrayHeight(null);
    setSheetSnap(nearestSnap?.key || "collapsed");
  }, [snapHeights]);

  const onHandlePointerUp = useCallback((e) => {
    finishSheetDrag(e.clientY);
  }, [finishSheetDrag]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handlePointerMove = (event) => {
      if (!dragRef.current.active) return;
      const delta = dragRef.current.startY - event.clientY;
      const nextHeight = clamp(
        dragRef.current.startHeight + delta,
        snapHeights.collapsed,
        snapHeights.full
      );
      setDragTrayHeight(nextHeight);
    };

    const handlePointerUp = (event) => {
      finishSheetDrag(event.clientY);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [finishSheetDrag, snapHeights]);

  // ── History-picker panel (when historyMode and no route loaded yet) ────────
  if (tripData?.historyMode && !internalRoute) {
    return (
      <div
        ref={panelRef}
        className="trip-playback-shell"
        style={{
          "--replay-left-offset": `${sidebarOffset}px`,
          "--replay-right-offset": `${rightOffset}px`,
        }}
      >
        <div className="trip-playback-sheet trip-playback-sheet--picker">
          {/* Header */}
          <div style={{ padding:"14px 20px 10px",background:"linear-gradient(135deg,#ff4757,#ff6b7a)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0 }}>
            <div>
              <div style={{ fontWeight:800,fontSize:16 }}>Trip History</div>
              <div style={{ fontSize:12,opacity:0.85,marginTop:2 }}>{tripData.deviceName || "Vehicle"}</div>
            </div>
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.2)",border:"none",borderRadius:8,color:"#fff",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
              <X size={16}/>
            </button>
          </div>
          {/* Date filters */}
          <div style={{ display:"flex",gap:10,padding:"12px 16px",borderBottom:"1px solid #e5e7eb",alignItems:"center",flexShrink:0,flexWrap:"wrap" }}>
            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
              <label style={{ fontSize:12,color:"#6b7280",fontWeight:600 }}>From</label>
              <input type="date" value={histStart} max={todayStr} onChange={e=>setHistStart(e.target.value)}
                style={{ border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13,outline:"none" }}/>
            </div>
            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
              <label style={{ fontSize:12,color:"#6b7280",fontWeight:600 }}>To</label>
              <input type="date" value={histEnd} min={histStart} max={todayStr} onChange={e=>setHistEnd(e.target.value)}
                style={{ border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13,outline:"none" }}/>
            </div>
            <button onClick={()=>fetchHistoryTrips(histStart,histEnd)} disabled={histLoading}
              style={{ background:"linear-gradient(135deg,#ff4757,#ff6b7a)",color:"#fff",border:"none",borderRadius:8,padding:"7px 18px",fontWeight:700,fontSize:13,cursor:"pointer",opacity:histLoading?0.7:1 }}>
              {histLoading ? "Loading…" : "Search"}
            </button>
          </div>
          {/* Trip list */}
          <div style={{ flex:1,overflow:"auto" }}>
            {histError && <div style={{ padding:16,color:"#ef4444",fontSize:13 }}>Error: {histError}</div>}
            {!histLoading && !histError && histTrips.length===0 && (
              <div style={{ padding:32,textAlign:"center",color:"#9ca3af",fontSize:13 }}>No trips found for selected dates</div>
            )}
            {histTrips.map(trip=>(
              <div key={trip.id} onClick={()=>setHistSelId(trip.id)}
                style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:"1px solid #f3f4f6",cursor:"pointer",background:histSelId===trip.id?"#fff1f2":"#fff",borderLeft:histSelId===trip.id?"3px solid #ef4444":"3px solid transparent" }}>
                <div>
                  <div style={{ fontWeight:700,fontSize:13,color:"#111827" }}>{trip.date}</div>
                  <div style={{ fontSize:11,color:"#6b7280",marginTop:2 }}>
                    {trip.distance} km &nbsp;·&nbsp; {formatDurationLabel(trip.duration)} &nbsp;·&nbsp; Max {trip.maxSpeed} km/h
                  </div>
                </div>
                {histSelId===trip.id && (
                  <button
                    onClick={e=>{e.stopPropagation();loadTripRoute(trip);}}
                    disabled={!!routeLoadingId}
                    style={{ background:"linear-gradient(135deg,#ff4757,#ff6b7a)",color:"#fff",border:"none",borderRadius:20,padding:"6px 16px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6,opacity:routeLoadingId?0.7:1 }}>
                    {routeLoadingId===trip.id ? "Loading…" : <><Play size={12}/> Play</>}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Compute derived values for render ─────────────────────────────────────
  if (route.length === 0) {
    return (
      <div
        className="trip-playback-shell"
        style={{
          "--replay-left-offset": `${sidebarOffset}px`,
          "--replay-right-offset": `${rightOffset}px`,
        }}
      >
        <div className="trip-playback-empty">
        <span style={{ fontSize:14,fontWeight:700,color:"#ef4444" }}>No Route Data</span>
        <button onClick={onClose} style={{ background:"#f3f4f6",border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",fontWeight:700,fontSize:13 }}>Close</button>
        </div>
      </div>
    );
  }

  // ── Style helpers ──────────────────────────────────────────────────────────
  const R = "#ef4444";
  const redCircleBtn = {
    width:38,height:38,borderRadius:"50%",border:"none",cursor:"pointer",
    background:"linear-gradient(135deg,#ff4757,#ff6b7a)",color:"#fff",
    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
    boxShadow:"0 3px 10px rgba(239,68,68,0.35)",
  };
  const th = { textAlign:"left",padding:"10px 12px",borderBottom:"1px solid #e5e7eb",color:"#111827",fontWeight:700,fontSize:12,background:"#f8fafc",whiteSpace:"nowrap" };
  const td = { padding:"9px 12px",borderBottom:"1px solid #f3f4f6",fontSize:12,color:"#374151",verticalAlign:"middle" };
  const tabBtn = (key) => ({
    padding:"10px 14px",border:"none",borderBottom:`2px solid ${activeTab===key?R:"transparent"}`,
    background:"transparent",cursor:"pointer",fontWeight:700,fontSize:13,
    color:activeTab===key?R:"#6b7280",whiteSpace:"nowrap",
  });
  const expandBtn = {
    width:28,height:28,borderRadius:6,border:"none",background:"#f1f5f9",
    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#6b7280",
    flexShrink:0,
  };
  const pageBtn = (active) => ({
    minWidth:32,padding:"5px 8px",border:"1px solid #d1d5db",borderRadius:6,
    background:active?"#111827":"#fff",color:active?"#fff":"#111827",
    cursor:active?"default":"pointer",fontWeight:700,fontSize:12,
  });

  const effectiveTrayHeight = Math.round(
    clamp(
      dragTrayHeight ?? snapHeights[sheetSnap] ?? snapHeights.collapsed,
      snapHeights.collapsed,
      snapHeights.full
    )
  );
  const sheetOpen = effectiveTrayHeight > snapHeights.collapsed + 18;
  const trayHeight = `${effectiveTrayHeight}px`;
  const showTrayContent = effectiveTrayHeight > snapHeights.collapsed + 44;

  return (
    <div
      ref={panelRef}
      className={`trip-playback-shell ${sheetOpen ? "is-expanded" : "is-collapsed"} ${isDraggingSheet ? "is-dragging" : ""}`}
      style={{
        "--replay-left-offset": `${sidebarOffset}px`,
        "--replay-right-offset": `${rightOffset}px`,
      }}
    >
      <div className="trip-playback-floating">
        <div className="trip-playback-card">
        <div style={{ padding:"10px 0 2px",display:"flex",justifyContent:"center",cursor:"ns-resize",flexShrink:0,userSelect:"none",touchAction:"none" }}
          onPointerDown={onHandlePointerDown}
          onPointerUp={onHandlePointerUp}>
          <div style={{ width:40,height:4,borderRadius:2,background:"#d1d5db" }}/>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 16px 8px",flexShrink:0 }}>
          <button onClick={handlePlayPause} style={{ width:64,height:64,borderRadius:"50%",border:"none",cursor:"pointer",flexShrink:0,background:"linear-gradient(135deg,#ff4757 0%,#ff6b7a 100%)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 8px 24px rgba(255,71,87,0.45)" }}>
            {isPlaying ? <Pause size={26}/> : <Play size={26}/>}
          </button>
          <div style={{ flex:1,height:6,background:"#fde8ea",borderRadius:3,cursor:"pointer",overflow:"hidden",position:"relative" }} onClick={handleProgressClick}>
            <div style={{ height:"100%",width:`${progress}%`,background:"linear-gradient(90deg,#ff4757,#ff8fa3)",borderRadius:3,transition:"width 0.1s linear" }}/>
          </div>
          <div style={{ display:"flex",gap:6,alignItems:"center",flexShrink:0 }}>
            <button onClick={()=>{const i=PLAYBACK_SPEEDS.indexOf(playbackSpeed);setPlaybackSpeed(PLAYBACK_SPEEDS[(i+1)%PLAYBACK_SPEEDS.length]);}} style={{ ...redCircleBtn,width:"auto",height:38,padding:"0 14px",borderRadius:999,fontSize:13,fontWeight:800 }}>{playbackSpeed}x</button>
            <button onClick={handleStepBack} title="Previous" style={redCircleBtn}><ChevronLeft size={16}/></button>
            <button onClick={handleReset} title="Reset" style={redCircleBtn}><RotateCcw size={16}/></button>
            <button onClick={handleStepForward} title="Next" style={redCircleBtn}><ChevronRight size={16}/></button>
            <button onClick={()=>{setFollowVehicle(p=>!p);if(map&&currentPosition)map.panTo({lat:currentPosition.lat,lng:currentPosition.lng});}} title={followVehicle?"Unfollow":"Follow"} style={{ ...redCircleBtn,background:followVehicle?"linear-gradient(135deg,#ff4757,#ff6b7a)":"#f3f4f6",color:followVehicle?"#fff":"#6b7280",boxShadow:followVehicle?"0 3px 10px rgba(239,68,68,0.35)":"none" }}><LocateFixed size={16}/></button>
            <button onClick={onClose} title="Close" style={{ ...redCircleBtn,background:"#f3f4f6",color:"#6b7280",boxShadow:"none" }}><X size={16}/></button>
          </div>
        </div>

        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px 8px",flexShrink:0,gap:8 }}>
          <div style={{ display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0,overflow:"hidden" }}>
            <MapPin size={14} style={{ color:R,flexShrink:0 }}/>
            <span style={{ fontSize:13,color:"#374151",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
              {getReplayAddress(currentPointMeta)}
            </span>
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:6,flexShrink:0 }}>
            <Clock size={14} style={{ color:R }}/>
            <span style={{ fontSize:13,color:"#374151",whiteSpace:"nowrap" }}>{formatDateTime(currentPointMeta?.timestampMs||currentPosition?.timestamp)}</span>
          </div>
        </div>

        <div style={{ display:"flex",borderTop:"1px solid #f3f4f6",borderBottom:"1px solid #f3f4f6",background:"#fafbfc",flexShrink:0 }}>
          {[
            { icon:<span style={{fontSize:15}}>⚡</span>, value:`${Math.round(currentPosition?.voltage||currentPosition?.power||0)}v`, color:"#f97316" },
            { icon:<Gauge size={14}/>, value:`${Math.round(currentPosition?.speed||0)} Kmh`, color:R },
            { icon:<Clock size={14}/>, value:tripData.durationLabel||formatDurationLabel(Number(tripData.durationSeconds||0)), color:"#6b7280" },
            { icon:<Gauge size={14}/>, value:`${Math.round(summaryStats.avgSpeed)} Kmh`, color:R },
            { icon:<Navigation size={14}/>, value:`${Number(tripData.distance||0).toFixed(2)} Km`, color:R },
          ].map((item,i)=>(
            <div key={i} style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"9px 4px",borderRight:i<4?"1px solid #efefef":"none" }}>
              <span style={{ color:item.color,display:"flex",alignItems:"center" }}>{item.icon}</span>
              <span style={{ fontSize:12,fontWeight:700,color:"#111827" }}>{item.value}</span>
            </div>
          ))}
        </div>
        </div>
      </div>

      <div
        className={`trip-playback-tray ${sheetOpen ? "is-open" : "is-collapsed"}`}
        style={{ height: trayHeight }}
      >
        <div className="trip-playback-tabs" style={{ display:"flex",alignItems:"center",borderBottom:"1px solid #e5e7eb",background:"#fff",flexShrink:0 }}>
          {["summary","logs"].map(key=>(
            <button key={key} onClick={()=>{setActiveTab(key);if(!sheetOpen)setSheetSnap("half");}} style={tabBtn(key)}>
              {key.charAt(0).toUpperCase()+key.slice(1)}
            </button>
          ))}
          <div style={{ flex:1 }}/>
          <button onClick={()=>setSheetSnap(s=>s==="collapsed"?"half":"collapsed")} style={expandBtn}>
            {sheetOpen ? <ChevronDown size={15}/> : <ChevronUp size={15}/>}
          </button>
          {["parking","events"].map(key=>(
            <button key={key} onClick={()=>{setActiveTab(key);if(!sheetOpen)setSheetSnap("half");}} style={tabBtn(key)}>
              {key.charAt(0).toUpperCase()+key.slice(1)}
            </button>
          ))}
        </div>

        {showTrayContent && (
          <div className="trip-playback-tray-content" style={{ flex:1,overflow:"auto",WebkitOverflowScrolling:"touch",background:"#fff" }}>

          {activeTab==="logs" && (
            <>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:900 }}>
                  <thead style={{ position:"sticky",top:0,zIndex:1 }}>
                    <tr>
                      {["Status","Fix Time","Latitude","Longitude","Speed","Address"].map(h=>(
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedLogRows.map(row=>(
                      <tr
                        key={row.id}
                        onClick={()=>jumpToRow(row.index)}
                        style={{ background:row.index===currentIndex?"#fff1f2":"transparent",cursor:"pointer" }}
                      >
                        <td style={td}>
                          <span style={{ fontWeight:700,color:getStatusColor(row.status) }}>{row.status}</span>
                        </td>
                        <td style={{ ...td,whiteSpace:"nowrap" }}>
                          <span style={{ display:"inline-flex",alignItems:"center",gap:5 }}>
                            <span style={{ fontSize:14 }}>🚗</span>
                            {row.fixTime}
                          </span>
                        </td>
                        <td style={{ ...td,fontFamily:"monospace" }}>{row.latitude}</td>
                        <td style={{ ...td,fontFamily:"monospace" }}>{row.longitude}</td>
                        <td style={{ ...td,whiteSpace:"nowrap" }}>{row.speed}</td>
                        <td style={{ ...td,minWidth:200 }}>{row.address}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderTop:"1px solid #e5e7eb",background:"#fff",flexWrap:"wrap",gap:8 }}>
                <span style={{ fontSize:12,color:"#374151" }}>
                  Showing {logRowCount ? logStartIndex+1 : 0} to {logEndIndex} of {logRowCount} entries
                </span>
                <div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>
                  <button onClick={()=>setLogPage(p=>Math.max(1,p-1))} disabled={safeLogPage<=1}
                    style={{ ...pageBtn(false),opacity:safeLogPage<=1?0.5:1,cursor:safeLogPage<=1?"not-allowed":"pointer" }}>
                    &laquo; Previous
                  </button>
                  {logPageButtons.map(btn=>(
                    <button key={btn.key} onClick={()=>btn.value&&setLogPage(btn.value)} disabled={!btn.value}
                      style={{ ...pageBtn(btn.value===safeLogPage),cursor:btn.value?"pointer":"default",opacity:btn.value?1:0.6 }}>
                      {btn.label}
                    </button>
                  ))}
                  <button onClick={()=>setLogPage(p=>Math.min(logPageCount,p+1))} disabled={safeLogPage>=logPageCount}
                    style={{ ...pageBtn(false),opacity:safeLogPage>=logPageCount?0.5:1,cursor:safeLogPage>=logPageCount?"not-allowed":"pointer" }}>
                    Next &raquo;
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab==="summary" && (
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,padding:14 }}>
              {[
                ["Total Points",summaryStats.pointCount],
                ["Top Speed",`${Math.round(summaryStats.topSpeed)} km/h`],
                ["Avg Speed",`${Math.round(summaryStats.avgSpeed)} km/h`],
                ["Idle Points",summaryStats.idlePoints],
                ["Parking Sessions",parkingRows.length],
                ["Events",eventRows.length],
                ["Start Time",formatDateTime(route[0]?.timestampMs||route[0]?.timestamp||tripData.startTime)],
                ["End Time",formatDateTime(route[route.length-1]?.timestampMs||route[route.length-1]?.timestamp||tripData.endTime)],
              ].map(([label,value])=>(
                <div key={label} style={{ ...cardStyle,padding:"10px 12px" }}>
                  <div style={{ fontSize:10,color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3 }}>{label}</div>
                  <div style={{ fontWeight:700,color:"#111827",fontSize:14 }}>{value}</div>
                </div>
              ))}
            </div>
          )}

          {activeTab==="parking" && (
            <div style={{ padding:12 }}>
              {parkingRows.length===0
                ? <div style={{ padding:24,textAlign:"center",color:"#9ca3af",fontSize:13 }}>No parking sessions detected</div>
                : parkingRows.map(row=>(
                    <div key={row.id} onClick={()=>jumpToRow(row.startIndex)}
                      style={{ ...cardStyle,marginBottom:8,cursor:"pointer",background:row.startIndex===currentIndex?"#fff1f2":"#fff",borderLeft:"3px solid #f97316" }}>
                      <div style={{ fontWeight:700,fontSize:12,marginBottom:3 }}>{formatDateTime(row.startTs)} → {formatDateTime(row.endTs)}</div>
                      <div style={{ fontSize:11,color:"#6b7280",marginBottom:1 }}>Duration: {formatDurationLabel(row.durationSeconds)}</div>
                      <div style={{ fontSize:11,color:"#374151" }}>{row.address || ADDRESS_FALLBACK_LABEL}</div>
                    </div>
                  ))
              }
            </div>
          )}

          {activeTab==="events" && (
            <div style={{ padding:12 }}>
              {eventRows.length===0
                ? <div style={{ padding:24,textAlign:"center",color:"#9ca3af",fontSize:13 }}>No events found</div>
                : eventRows.map(row=>(
                    <div key={row.id} onClick={()=>jumpToRow(row.index)}
                      style={{ ...cardStyle,marginBottom:8,cursor:"pointer",background:row.index===currentIndex?"#fff1f2":"#fff",borderLeft:"3px solid #ef4444" }}>
                      <div style={{ fontWeight:700,fontSize:12,marginBottom:3,textTransform:"capitalize" }}>{row.type}</div>
                      <div style={{ fontSize:11,color:"#6b7280",marginBottom:1 }}>{row.time}</div>
                      <div style={{ fontSize:11,color:"#6b7280",marginBottom:1 }}>Speed: {row.speed}</div>
                      <div style={{ fontSize:11,color:"#374151" }}>{row.address}</div>
                    </div>
                  ))
              }
            </div>
          )}

        </div>
        )}
      </div>
    </div>
  );
};

export default TripPlayback;
